import React, { useState, useEffect, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export const PdfSigningViewer = ({
  pdfDataUrl,
  onSignatureCaptured,
  contractTitle,
  contractRef,
  approverEmail,
  approverName,
  approvalOrder,
  totalApprovers,
  preAssignedSignature,
  isSubmitting = false
}) => {
  const pdfCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  // Actual page size in PDF points (1 point = 1 unit at scale 1), read from
  // the rendered page rather than assumed — pages that aren't US Letter
  // (612x792), e.g. A4 (595x842), would otherwise get their signature/text
  // Y position miscalculated against the wrong page height.
  const [pdfPageSize, setPdfPageSize] = useState({ width: 612, height: 792 });
  const [mode, setMode] = useState("view"); // "view", "text", "signature"
  const [signatureData, setSignatureData] = useState(null);
  const [textBoxes, setTextBoxes] = useState([]);
  const [activeTextBox, setActiveTextBox] = useState(null);
  const [editingTextBox, setEditingTextBox] = useState(null);
  const [activeSignature, setActiveSignature] = useState(null);
  const [draggingTextBox, setDraggingTextBox] = useState(null);
  const [cursor, setCursor] = useState("default");
  const textInputRef = useRef(null);

  // Signature positioning state
  const [signature, setSignature] = useState({
    x: 50,
    y: 100,
    width: 150,
    height: 60,
    isDragging: false,
    isResizing: false,
    dragOffsetX: 0,
    dragOffsetY: 0,
    resizeHandle: null
  });

  useEffect(() => {
    loadPdf();
  }, [pdfDataUrl]);

  // Auto-load pre-assigned signature
  useEffect(() => {
    if (preAssignedSignature) {
      setSignatureData(preAssignedSignature);
      console.log("[PdfSigningViewer] ✓ Auto-loaded pre-assigned signature");
    }
  }, [preAssignedSignature]);

  useEffect(() => {
    if (pdfDoc && currentPage) {
      const timer = setTimeout(() => {
        renderPage(currentPage, pdfDoc);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [scale, pdfDoc, currentPage]);

  // Redraw overlay when signature or text boxes change
  useEffect(() => {
    if (pdfDoc && currentPage) {
      setTimeout(() => drawOverlay(), 50);
    }
  }, [signature, textBoxes, activeTextBox, editingTextBox, signatureData, mode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape: Exit edit mode and return to view mode
      if (e.key === "Escape") {
        if (editingTextBox) {
          setEditingTextBox(null);
          setMode("view");
          e.preventDefault();
        }
      }

      // Delete: Delete active text box
      if (e.key === "Delete" && activeTextBox && !editingTextBox) {
        deleteTextBox(activeTextBox);
        e.preventDefault();
      }

      // Enter: Confirm edit (only when editing)
      if (e.key === "Enter" && editingTextBox) {
        setEditingTextBox(null);
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeTextBox, editingTextBox]);

  // Update cursor based on hover state
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!overlayCanvasRef.current) return;

      const rect = overlayCanvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      let newCursor = "default";

      if (mode === "text") {
        newCursor = "crosshair";

        // Check if hovering over text box
        const hoveredBox = textBoxes.find(box =>
          x >= box.x && x <= box.x + box.width &&
          y >= box.y && y <= box.y + box.height
        );

        if (hoveredBox) {
          const handleSize = 8;
          const handles = [
            { corner: "tl", x: hoveredBox.x - handleSize / 2, y: hoveredBox.y - handleSize / 2, cursor: "nwse-resize" },
            { corner: "tr", x: hoveredBox.x + hoveredBox.width - handleSize / 2, y: hoveredBox.y - handleSize / 2, cursor: "nesw-resize" },
            { corner: "bl", x: hoveredBox.x - handleSize / 2, y: hoveredBox.y + hoveredBox.height - handleSize / 2, cursor: "nesw-resize" },
            { corner: "br", x: hoveredBox.x + hoveredBox.width - handleSize / 2, y: hoveredBox.y + hoveredBox.height - handleSize / 2, cursor: "nwse-resize" }
          ];

          const hoveredHandle = handles.find(h =>
            x >= h.x && x <= h.x + handleSize &&
            y >= h.y && y <= h.y + handleSize
          );

          if (hoveredHandle) {
            newCursor = hoveredHandle.cursor;
          } else {
            newCursor = "grab";
          }
        }
      } else if (mode === "signature" && signatureData) {
        newCursor = "default";

        // Check if hovering over signature
        if (
          x >= signature.x &&
          x <= signature.x + signature.width &&
          y >= signature.y &&
          y <= signature.y + signature.height
        ) {
          const handleSize = 16;
          const handles = [
            { corner: "tl", x: signature.x - handleSize / 2, y: signature.y - handleSize / 2, cursor: "nwse-resize" },
            { corner: "tr", x: signature.x + signature.width - handleSize / 2, y: signature.y - handleSize / 2, cursor: "nesw-resize" },
            { corner: "bl", x: signature.x - handleSize / 2, y: signature.y + signature.height - handleSize / 2, cursor: "nesw-resize" },
            { corner: "br", x: signature.x + signature.width - handleSize / 2, y: signature.y + signature.height - handleSize / 2, cursor: "nwse-resize" }
          ];

          const hoveredHandle = handles.find(h =>
            x >= h.x && x <= h.x + handleSize &&
            y >= h.y && y <= h.y + handleSize
          );

          if (hoveredHandle) {
            newCursor = hoveredHandle.cursor;
          } else {
            newCursor = "grab";
          }
        }
      }

      setCursor(newCursor);
    };

    if (overlayCanvasRef.current) {
      overlayCanvasRef.current.addEventListener("mousemove", handleMouseMove);
    }

    return () => {
      if (overlayCanvasRef.current) {
        overlayCanvasRef.current.removeEventListener("mousemove", handleMouseMove);
      }
    };
  }, [mode, textBoxes, signature, signatureData]);

  const loadPdf = async () => {
    try {
      if (!pdfDataUrl) return;
      const pdf = await pdfjsLib.getDocument({ url: pdfDataUrl }).promise;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      renderPage(1, pdf);
    } catch (error) {
      console.error("[PdfSigningViewer] Error loading PDF:", error);
    }
  };

  const renderPage = async (pageNum, pdf = pdfDoc) => {
    if (!pdf) return;

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
    }

    const canvas = pdfCanvasRef.current;
    if (!canvas) {
      setTimeout(() => renderPage(pageNum, pdf), 100);
      return;
    }

    try {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale });

      // Capture this page's true size in PDF points (scale 1), used to convert
      // the on-screen drop position back to accurate PDF coordinates later —
      // must not assume a fixed page size, since it varies (A4 vs Letter, etc.)
      // and can even differ page to page within the same document.
      const trueSize = page.getViewport({ scale: 1 });
      setPdfPageSize({ width: trueSize.width, height: trueSize.height });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Also set overlay canvas size
      if (overlayCanvasRef.current) {
        overlayCanvasRef.current.width = viewport.width;
        overlayCanvasRef.current.height = viewport.height;
      }

      const ctx = canvas.getContext("2d");
      const renderTask = page.render({ canvasContext: ctx, viewport });

      renderTaskRef.current = renderTask;
      await renderTask.promise;
      drawOverlay();
    } catch (error) {
      if (error.name !== "RenderingCancelledException") {
        console.error("[PdfSigningViewer] Error rendering PDF:", error);
      }
    }
  };

  const drawOverlay = () => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw text boxes
    textBoxes.forEach((box) => {
      const isActive = activeTextBox === box.id;

      // Draw box border
      ctx.strokeStyle = isActive ? "#3b82f6" : "#60a5fa";
      ctx.fillStyle = isActive ? "rgba(59, 130, 246, 0.1)" : "rgba(96, 165, 250, 0.05)";
      ctx.lineWidth = 2;
      ctx.fillRect(box.x, box.y, box.width, box.height);
      ctx.strokeRect(box.x, box.y, box.width, box.height);

      // Draw text
      ctx.fillStyle = "#1e293b";
      ctx.font = "12px sans-serif";
      ctx.textBaseline = "top";
      const padding = 4;
      ctx.fillText(box.text, box.x + padding, box.y + padding);

      // Draw resize handles if active and in text mode
      if (isActive && mode === "text") {
        const handleSize = 6;
        const handles = [
          { x: box.x - handleSize / 2, y: box.y - handleSize / 2, cursor: "nw-resize" },
          { x: box.x + box.width - handleSize / 2, y: box.y - handleSize / 2, cursor: "ne-resize" },
          { x: box.x - handleSize / 2, y: box.y + box.height - handleSize / 2, cursor: "sw-resize" },
          { x: box.x + box.width - handleSize / 2, y: box.y + box.height - handleSize / 2, cursor: "se-resize" }
        ];

        ctx.fillStyle = "#fbbf24";
        ctx.strokeStyle = "#f59e0b";
        ctx.lineWidth = 1;
        handles.forEach((handle) => {
          ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
          ctx.strokeRect(handle.x, handle.y, handleSize, handleSize);
        });
      }
    });

    // Draw signature (always, so it shows in all modes)
    if (signatureData) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, signature.x, signature.y, signature.width, signature.height);

        // Draw border only in signature mode
        if (mode === "signature") {
          ctx.strokeStyle = signature.isDragging || signature.isResizing ? "#10b981" : "#34d399";
          ctx.lineWidth = 2;
          ctx.strokeRect(signature.x, signature.y, signature.width, signature.height);

          // Draw resize handles
          const handleSize = 8;
          const handles = [
            { x: signature.x - handleSize / 2, y: signature.y - handleSize / 2 },
            { x: signature.x + signature.width - handleSize / 2, y: signature.y - handleSize / 2 },
            { x: signature.x - handleSize / 2, y: signature.y + signature.height - handleSize / 2 },
            { x: signature.x + signature.width - handleSize / 2, y: signature.y + signature.height - handleSize / 2 }
          ];

          ctx.fillStyle = "#10b981";
          ctx.strokeStyle = "#059669";
          ctx.lineWidth = 1;
          handles.forEach((handle) => {
            ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
            ctx.strokeRect(handle.x, handle.y, handleSize, handleSize);
          });
        }
      };
      img.src = signatureData;
    }
  };

  const handleCanvasClick = (e) => {
    if (mode !== "text") return;

    const rect = overlayCanvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if clicking on existing text box
    const clickedBox = textBoxes.find(box =>
      x >= box.x && x <= box.x + box.width &&
      y >= box.y && y <= box.y + box.height
    );

    if (clickedBox) {
      setActiveTextBox(clickedBox.id);

      // Double-click to enter edit mode
      if (e.detail === 2) {
        setEditingTextBox(clickedBox.id);
        setTimeout(() => textInputRef.current?.focus(), 0);
      }
    } else if (e.detail === 1) {
      // Single click in empty space creates new box
      const newTextBox = {
        id: Date.now(),
        x,
        y,
        width: 200,
        height: 40,
        text: ""
      };

      setTextBoxes([...textBoxes, newTextBox]);
      setActiveTextBox(newTextBox.id);
    }
  };

  const handleMouseDown = (e) => {
    if (mode === "text") {
      const rect = overlayCanvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Check if clicking on a text box
      const clickedBox = textBoxes.find(box =>
        x >= box.x && x <= box.x + box.width &&
        y >= box.y && y <= box.y + box.height
      );

      if (clickedBox) {
        const handleSize = 8;

        // Check if clicking on resize handles
        const handles = [
          { corner: "tl", x: clickedBox.x - handleSize / 2, y: clickedBox.y - handleSize / 2 },
          { corner: "tr", x: clickedBox.x + clickedBox.width - handleSize / 2, y: clickedBox.y - handleSize / 2 },
          { corner: "bl", x: clickedBox.x - handleSize / 2, y: clickedBox.y + clickedBox.height - handleSize / 2 },
          { corner: "br", x: clickedBox.x + clickedBox.width - handleSize / 2, y: clickedBox.y + clickedBox.height - handleSize / 2 }
        ];

        const resizeHandle = handles.find(h =>
          x >= h.x && x <= h.x + handleSize &&
          y >= h.y && y <= h.y + handleSize
        );

        if (resizeHandle) {
          // Start resizing
          setDraggingTextBox({
            id: clickedBox.id,
            type: "resize",
            corner: resizeHandle.corner,
            startX: x,
            startY: y,
            startWidth: clickedBox.width,
            startHeight: clickedBox.height,
            boxX: clickedBox.x,
            boxY: clickedBox.y
          });
        } else {
          // Start dragging
          setDraggingTextBox({
            id: clickedBox.id,
            type: "drag",
            offsetX: x - clickedBox.x,
            offsetY: y - clickedBox.y
          });
        }

        setActiveTextBox(clickedBox.id);
      }
    } else if (mode === "signature" && signatureData) {
      const rect = overlayCanvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const handleSize = 16;
      const handles = [
        { corner: "tl", x: signature.x - handleSize / 2, y: signature.y - handleSize / 2 },
        { corner: "tr", x: signature.x + signature.width - handleSize / 2, y: signature.y - handleSize / 2 },
        { corner: "bl", x: signature.x - handleSize / 2, y: signature.y + signature.height - handleSize / 2 },
        { corner: "br", x: signature.x + signature.width - handleSize / 2, y: signature.y + signature.height - handleSize / 2 }
      ];

      // Check if clicking a resize handle
      const resizeHandle = handles.find(h =>
        x >= h.x && x <= h.x + handleSize &&
        y >= h.y && y <= h.y + handleSize
      );

      if (resizeHandle) {
        setSignature(p => ({
          ...p,
          isResizing: true,
          resizeHandle: resizeHandle.corner,
          dragOffsetX: x,
          dragOffsetY: y
        }));
      } else if (
        x >= signature.x &&
        x <= signature.x + signature.width &&
        y >= signature.y &&
        y <= signature.y + signature.height
      ) {
        // Dragging signature
        setSignature(p => ({
          ...p,
          isDragging: true,
          dragOffsetX: x - signature.x,
          dragOffsetY: y - signature.y
        }));
      }
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!overlayCanvasRef.current) return;

      const rect = overlayCanvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Handle text box dragging/resizing
      if (mode === "text" && draggingTextBox) {
        if (draggingTextBox.type === "drag") {
          let newX = x - draggingTextBox.offsetX;
          let newY = y - draggingTextBox.offsetY;

          newX = Math.max(0, Math.min(newX, overlayCanvasRef.current.width - 100));
          newY = Math.max(0, Math.min(newY, overlayCanvasRef.current.height - 30));

          updateTextBox(draggingTextBox.id, { x: newX, y: newY });
        } else if (draggingTextBox.type === "resize") {
          const minWidth = 100;
          const minHeight = 30;
          const deltaX = x - draggingTextBox.startX;
          const deltaY = y - draggingTextBox.startY;

          let newX = draggingTextBox.boxX;
          let newY = draggingTextBox.boxY;
          let newWidth = draggingTextBox.startWidth;
          let newHeight = draggingTextBox.startHeight;

          if (draggingTextBox.corner === "tl") {
            newX = Math.max(0, draggingTextBox.boxX + deltaX);
            newY = Math.max(0, draggingTextBox.boxY + deltaY);
            newWidth = Math.max(minWidth, draggingTextBox.startWidth - (newX - draggingTextBox.boxX));
            newHeight = Math.max(minHeight, draggingTextBox.startHeight - (newY - draggingTextBox.boxY));
          } else if (draggingTextBox.corner === "tr") {
            newY = Math.max(0, draggingTextBox.boxY + deltaY);
            newWidth = Math.max(minWidth, draggingTextBox.startWidth + deltaX);
            newHeight = Math.max(minHeight, draggingTextBox.startHeight - (newY - draggingTextBox.boxY));
          } else if (draggingTextBox.corner === "bl") {
            newX = Math.max(0, draggingTextBox.boxX + deltaX);
            newWidth = Math.max(minWidth, draggingTextBox.startWidth - (newX - draggingTextBox.boxX));
            newHeight = Math.max(minHeight, draggingTextBox.startHeight + deltaY);
          } else if (draggingTextBox.corner === "br") {
            newWidth = Math.max(minWidth, draggingTextBox.startWidth + deltaX);
            newHeight = Math.max(minHeight, draggingTextBox.startHeight + deltaY);
          }

          updateTextBox(draggingTextBox.id, { x: newX, y: newY, width: newWidth, height: newHeight });
        }
      }

      // Handle signature dragging/resizing
      if (mode === "signature" && signature.isDragging) {
        let newX = x - signature.dragOffsetX;
        let newY = y - signature.dragOffsetY;

        newX = Math.max(0, Math.min(newX, overlayCanvasRef.current.width - signature.width));
        newY = Math.max(0, Math.min(newY, overlayCanvasRef.current.height - signature.height));

        setSignature(p => ({
          ...p,
          x: newX,
          y: newY
        }));
      } else if (mode === "signature" && signature.isResizing) {
        const minWidth = 80;
        const minHeight = 40;

        let newX = signature.x;
        let newY = signature.y;
        let newWidth = signature.width;
        let newHeight = signature.height;

        if (signature.resizeHandle === "tl") {
          newX = Math.min(x, signature.x + signature.width - minWidth);
          newY = Math.min(y, signature.y + signature.height - minHeight);
          newWidth = signature.x + signature.width - newX;
          newHeight = signature.y + signature.height - newY;
        } else if (signature.resizeHandle === "tr") {
          newY = Math.min(y, signature.y + signature.height - minHeight);
          newWidth = Math.max(minWidth, x - signature.x);
          newHeight = signature.y + signature.height - newY;
        } else if (signature.resizeHandle === "bl") {
          newX = Math.min(x, signature.x + signature.width - minWidth);
          newWidth = signature.x + signature.width - newX;
          newHeight = Math.max(minHeight, y - signature.y);
        } else if (signature.resizeHandle === "br") {
          newWidth = Math.max(minWidth, x - signature.x);
          newHeight = Math.max(minHeight, y - signature.y);
        }

        setSignature(p => ({
          ...p,
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight
        }));
      }
    };

    const handleMouseUp = () => {
      setDraggingTextBox(null);
      setSignature(p => ({
        ...p,
        isDragging: false,
        isResizing: false,
        resizeHandle: null
      }));
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [signature, mode, draggingTextBox, textBoxes]);

  const updateTextBox = (id, updates) => {
    setTextBoxes(textBoxes.map(box => box.id === id ? { ...box, ...updates } : box));
  };

  const deleteTextBox = (id) => {
    setTextBoxes(textBoxes.filter(box => box.id !== id));
    setActiveTextBox(null);
  };

  const handleSubmitSignature = () => {
    if (!signatureData) {
      alert("No signature available. Please contact your administrator.");
      return;
    }

    // Convert signature position to PDF coordinates — use this page's actual
    // size (captured on render), not an assumed one, so A4/Letter/etc. all
    // place the signature exactly where it was dropped on screen.
    const canvas = pdfCanvasRef.current;
    const pdfWidth = pdfPageSize.width;
    const pdfHeight = pdfPageSize.height;

    const signaturePdfX = Math.round((signature.x / canvas.width) * pdfWidth);
    const signaturePdfY = Math.round(pdfHeight - ((signature.y + signature.height) / canvas.height) * pdfHeight);
    const signaturePdfWidth = Math.round((signature.width / canvas.width) * pdfWidth);
    const signaturePdfHeight = Math.round((signature.height / canvas.height) * pdfHeight);

    // Convert text boxes to PDF coordinates
    const textBoxesPdf = textBoxes.map(box => ({
      ...box,
      pdfX: Math.round((box.x / canvas.width) * pdfWidth),
      pdfY: Math.round(pdfHeight - ((box.y + box.height / 2) / canvas.height) * pdfHeight),
      pdfWidth: Math.round((box.width / canvas.width) * pdfWidth),
      pdfHeight: Math.round((box.height / canvas.height) * pdfHeight)
    }));

    console.log('[PdfSigningViewer] 📊 Submitting signature and text:', {
      signature: { x: signaturePdfX, y: signaturePdfY, width: signaturePdfWidth, height: signaturePdfHeight },
      textBoxes: textBoxesPdf
    });

    onSignatureCaptured({
      signatureData: signatureData,
      signaturePage: currentPage,
      textBoxes: textBoxesPdf,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      signaturePosition: {
        x: signaturePdfX,
        y: signaturePdfY,
        width: signaturePdfWidth,
        height: signaturePdfHeight
      }
    });
  };

  return (
    <div className="w-full h-screen bg-slate-950 flex flex-col md:flex-row overflow-hidden">
      {/* PDF Viewer - Full width on mobile, left side on desktop */}
      <div
        ref={containerRef}
        className="flex-1 flex flex-col bg-slate-900 overflow-auto md:border-r border-slate-700 touch-manipulation"
      >
        {/* Mode Tabs */}
        <div className="bg-slate-800 border-b border-slate-700 p-2 flex gap-2">
          <button
            onClick={() => setMode("view")}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              mode === "view"
                ? "bg-yellow-600 text-white"
                : "bg-slate-700 text-slate-300 hover:bg-slate-600"
            }`}
          >
            👁️ View
          </button>
          <button
            onClick={() => setMode("text")}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              mode === "text"
                ? "bg-blue-600 text-white"
                : "bg-slate-700 text-slate-300 hover:bg-slate-600"
            }`}
          >
            ✏️ Text Tool
          </button>
          {preAssignedSignature && (
            <button
              onClick={() => setMode("signature")}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                mode === "signature"
                  ? "bg-green-600 text-white"
                  : "bg-slate-700 text-slate-300 hover:bg-slate-600"
              }`}
            >
              🖊️ Position Signature
            </button>
          )}
        </div>

        {/* PDF Canvas */}
        <div className="flex-1 flex items-start justify-center p-2 md:p-4 overflow-auto bg-black/20">
          <div className="relative">
            <canvas
              ref={pdfCanvasRef}
              className="max-w-full max-h-full"
              style={{ display: "block" }}
            />
            {/* Overlay Canvas for interactive elements */}
            <canvas
              ref={overlayCanvasRef}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              className="absolute top-0 left-0 max-w-full max-h-full"
              style={{
                display: "block",
                cursor: cursor
              }}
            />

            {/* Direct Text Input Overlay - appears when double-clicking */}
            {mode === "text" && editingTextBox && (
              (() => {
                const activeBox = textBoxes.find(box => box.id === editingTextBox);
                if (!activeBox) return null;

                return (
                  <input
                    ref={textInputRef}
                    type="text"
                    value={activeBox.text}
                    onChange={(e) => updateTextBox(activeBox.id, { text: e.target.value })}
                    onBlur={() => setEditingTextBox(null)}
                    autoFocus
                    className="absolute bg-white text-slate-900 font-semibold border-2 border-green-500 rounded text-sm p-2 focus:outline-none shadow-lg"
                    style={{
                      left: `${activeBox.x}px`,
                      top: `${activeBox.y}px`,
                      width: `${activeBox.width}px`,
                      height: `${activeBox.height}px`,
                      zIndex: 50
                    }}
                    placeholder="Type text here..."
                  />
                );
              })()
            )}
          </div>
        </div>

        {/* PDF Controls */}
        <div className="bg-slate-800 border-t border-slate-700 p-3 flex flex-wrap gap-2 items-center justify-between">
          <div className="flex gap-2 items-center text-sm">
            <span className="text-slate-400">Page {currentPage} of {totalPages}</span>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-2 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 rounded text-xs"
            >
              ← Prev
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="px-2 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 rounded text-xs"
            >
              Next →
            </button>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setScale(Math.max(1, scale - 0.2))}
              className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs"
            >
              🔍-
            </button>
            <span className="text-xs text-slate-400 w-8 text-center">{Math.round(scale * 100)}%</span>
            <button
              onClick={() => setScale(Math.min(3, scale + 0.2))}
              className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs"
            >
              🔍+
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar Controls */}
      <div className="w-full md:w-80 bg-slate-800 border-t md:border-t-0 md:border-l border-slate-700 overflow-y-auto p-4 space-y-4">
        {/* Contract & Approver Info */}
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase">Contract</label>
            <p className="text-sm font-semibold text-slate-100 mt-1">{contractTitle}</p>
            <p className="text-xs text-slate-400">{contractRef}</p>
          </div>
          <div className="border-t border-slate-700 pt-3">
            <label className="text-xs font-semibold text-slate-400 uppercase">Approver {approvalOrder}/{totalApprovers}</label>
            <p className="text-sm font-semibold text-slate-100 mt-1">{approverName}</p>
            <p className="text-xs text-slate-400">{approverEmail}</p>
          </div>
        </div>

        {/* Text Tool Mode */}
        {mode === "text" && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase mb-2 block">Text Tool</label>

              {/* Keyboard Shortcuts Help */}
              <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-3 mb-3">
                <p className="text-xs font-semibold text-blue-300 mb-2">⌨️ Keyboard Shortcuts:</p>
                <div className="text-xs text-blue-200 space-y-1">
                  <p>• <span className="font-mono bg-slate-900 px-1 rounded">Double-Click</span> = Edit text</p>
                  <p>• <span className="font-mono bg-slate-900 px-1 rounded">Enter</span> = Confirm edit</p>
                  <p>• <span className="font-mono bg-slate-900 px-1 rounded">Delete</span> = Remove box</p>
                  <p>• <span className="font-mono bg-slate-900 px-1 rounded">Esc</span> = Exit & View Mode</p>
                </div>
              </div>
            </div>

            {textBoxes.length > 0 && (
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase mb-2 block">Text Boxes ({textBoxes.length})</label>
                <div className="space-y-2">
                  {textBoxes.map(box => (
                    <div key={box.id} className={`p-2 rounded text-xs space-y-2 transition-all ${
                      editingTextBox === box.id
                        ? "bg-green-700 border-2 border-green-500 shadow-lg"
                        : activeTextBox === box.id
                        ? "bg-blue-700 border border-blue-500"
                        : "bg-slate-700 border border-slate-600 hover:bg-slate-600"
                    }`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 truncate text-slate-100 font-medium">
                          {editingTextBox === box.id ? "✎ Editing..." : box.text || "🔸 (empty)"}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setActiveTextBox(box.id);
                            setEditingTextBox(box.id);
                            setTimeout(() => textInputRef.current?.focus(), 0);
                          }}
                          className="flex-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs whitespace-nowrap font-medium"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => deleteTextBox(box.id)}
                          className="flex-1 px-2 py-1 bg-red-900/50 hover:bg-red-900/70 text-red-300 rounded text-xs whitespace-nowrap"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Signature Mode */}
        {mode === "signature" && (
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase mb-2 block">Position Signature</label>
            <p className="text-xs text-green-400 mb-4">Drag signature to move it. Use handles to resize.</p>

            {preAssignedSignature && (
              <div className="bg-green-900/30 border border-green-600 rounded-lg p-4">
                <div className="text-sm text-green-400 font-medium mb-3">✓ Your Signature Ready</div>
                <img src={preAssignedSignature} alt="Your signature" className="h-16 object-contain mx-auto bg-slate-700 p-2 rounded mb-3" />
                <p className="text-xs text-slate-400">Position your signature on the PDF using drag and resize</p>
              </div>
            )}
          </div>
        )}

        {/* View Mode - Signature Status */}
        {mode === "view" && (
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase mb-2 block">Your Signature</label>
            {preAssignedSignature ? (
              <div className="bg-green-900/30 border border-green-600 rounded-lg p-4">
                <div className="text-sm text-green-400 font-medium mb-3">✓ Pre-assigned Signature</div>
                <img src={preAssignedSignature} alt="Your signature" className="h-16 object-contain mx-auto bg-slate-700 p-2 rounded" />
              </div>
            ) : (
              <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-4 text-center">
                <p className="text-sm text-yellow-400">⚠️ No signature assigned</p>
                <p className="text-xs text-slate-400 mt-2">Contact your administrator</p>
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        {preAssignedSignature && (
          <button
            onClick={handleSubmitSignature}
            disabled={isSubmitting}
            className={`w-full px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              isSubmitting
                ? "bg-green-700/50 text-white cursor-not-allowed opacity-75"
                : "bg-green-700 hover:bg-green-800 text-white cursor-pointer"
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                ✓ Submit Signature & Text
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};
